import React, { useState, useCallback, useEffect } from 'react';

interface SearchBarProps {
  value?: string;
  onSearch: (term: string) => void;
  placeholder?: string;
  debounce?: number;
  disabled?: boolean;
}

/**
 * Search bar with debounced input
 * Optimized for event use (iPad, touchscreens)
 */
export function SearchBar({
  value = '',
  onSearch,
  placeholder = 'Search products...',
  debounce = 300,
  disabled = false,
}: SearchBarProps) {
  const [searchTerm, setSearchTerm] = useState(value);

  // Update internal state when prop changes
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Debounce search
  useEffect(() => {
    if (disabled) return;

    const timer = setTimeout(() => {
      if (searchTerm !== value) {
        onSearch(searchTerm);
      }
    }, debounce);

    return () => clearTimeout(timer);
  }, [searchTerm, debounce, disabled, value, onSearch]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearch(searchTerm);
    },
    [searchTerm, onSearch]
  );

  const handleClear = useCallback(() => {
    setSearchTerm('');
    onSearch('');
  }, [onSearch]);

  return (
    <form onSubmit={handleSubmit} className="relative w-full max-w-2xl">
      <div className="relative">
        {/* Search icon */}
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          <svg
            className="h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Input field */}
        <input
          type="text"
          value={searchTerm}
          onChange={handleChange}
          disabled={disabled}
          className={`
            block w-full rounded-lg border-0 py-4 pl-12 pr-12
            text-base text-gray-900 ring-1 ring-inset
            ${
              disabled
                ? 'bg-gray-100 text-gray-400 ring-gray-200'
                : 'bg-white ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-600'
            }
            touch-min-height sm:text-sm sm:leading-6
          `}
          placeholder={placeholder}
          role="searchbox"
          aria-label="Search products"
        />

        {/* Clear button */}
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600"
            aria-label="Clear search"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </form>
  );
}
